import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  MotifModal,
  MotifModalBody,
  MotifModalHeader,
  MotifIcon,
  MotifProgressLoader,
} from "@ey-xd/motif-react";
import {
  IconoirCheckCircle,
  MotifAlertIcErrorOutline24px,
} from "@ey-xd/motif-icon";
import {
  useUpdateSchemeEffectiveMutation,
  useLazyReadSchemeProcessIngStatusQuery,
} from "@/services/scheme/schemeReducer";
import { useLazyExportDocumentQuery } from "@/services/exports/exportsReducer";
import { commonFormateName } from "@/utility/common";

import "./SchemeEffectivePopup.scss";

const SchemeEffectivePopup = ({
  showModal,
  schemeVersionId,
  schemeName,
  schemeId,
  abbreviation,
  caseShortName,
  schemeVersionRefetch,
  onClose,
  setFileDownloadStatus,
  getSchemeStatus,
  setStatusMsg,
}: {
  showModal: boolean;
  schemeVersionId: string;
  schemeName: string;
  schemeId: string | undefined;
  abbreviation: string | undefined;
  caseShortName: string | undefined;
  schemeVersionRefetch: () => void;
  onClose: () => void;
  setFileDownloadStatus: (status: string) => void;
  getSchemeStatus: (response: any) => void;
  setStatusMsg: (status: string) => void;
}) => {
  const { t } = useTranslation();
  const [uploadStatus, setUploadStatus] = useState("");
  const [showProgress, setShowProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [updateSchemeEffective] = useUpdateSchemeEffectiveMutation();
  const [getSchemeProcessingStatus, { data }] =
    useLazyReadSchemeProcessIngStatusQuery();
  const [exportDocument] = useLazyExportDocumentQuery();
  const initialRender = useRef(true);

  useEffect(() => {
    const makeSchemeEffective = async () => {
      if (initialRender.current) {
        initialRender.current = false;
        if (showModal) {
          setIsLoading(true);
          try {
            const response: any = await updateSchemeEffective(schemeVersionId);
            setIsLoading(false);
            if (response.data) {
              await getSchemeProcessingStatus(schemeVersionId).then(
                (response: any) => {
                  getSchemeStatus(response);
                }
              );
              setShowProgress(true);
              setUploadStatus("inProgress");
            } else {
              setUploadStatus("error");
              setErrorMessage(
                t("pages.configuration.scheme.makeSchemeEffectiveErrorMessage")
              );
            }
          } catch (error) {
            setIsLoading(false);
            setUploadStatus("error");
            setErrorMessage(
              t("pages.configuration.scheme.makeSchemeEffectiveErrorMessage")
            );
          }
        }
      }
    };
    makeSchemeEffective();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (
      showProgress &&
      (data?.processingStatus === 1 || data?.processingStatus === 2)
    ) {
      intervalId = setInterval(() => {
        getSchemeProcessingStatus(schemeVersionId).then((response: any) => {
          getSchemeStatus(response);
        });
      }, 10000);
    }
    if (showProgress && data?.processingStatus === 3) {
      setUploadStatus("error");
      setErrorMessage(data?.processingStatusMessage);
      setShowProgress(false);
      setStatusMsg("");
    } else if (showProgress && data?.processingStatus === 0) {
      setUploadStatus("Success");
      setShowProgress(false);
      exportDocument({
        schemeId,
        url: "/Scheme/ExportClaimItemClass",
        fileName: caseShortName
          ? commonFormateName(caseShortName, schemeName, abbreviation, "Scheme")
          : "",
      })
        .then((response) => {
          if (response?.error && "data" in response.error) {
            try {
              const errObj = JSON.parse(response?.error?.data as string);
              setFileDownloadStatus(errObj?.message || errObj.title);
            } catch (error) {
              setFileDownloadStatus("Something went wrong!!");
              console.error("File download error:", error);
            }
          }
        })
        .catch((error: any) => {
          setFileDownloadStatus("Something went wrong!!");
        });
      schemeVersionRefetch();
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    showProgress,
    getSchemeStatus,
    getSchemeProcessingStatus,
    data,
    schemeVersionId,
    schemeVersionRefetch,
    abbreviation,
    caseShortName,
    exportDocument,
    schemeId,
    schemeName,
    setFileDownloadStatus,
    setStatusMsg
  ]);

  return (
    <MotifModal
      onClose={onClose}
      show={showModal}
      focusTrapOptions={{
        tabbableOptions: {
          displayCheck: "none",
        },
      }}
    >
      <MotifModalHeader
        closeButtonProps={{
          "aria-label": "Custom Close Button aria-label",
          title: "Custom Close Button title",
        }}
      >
        {t("pages.configuration.scheme.makeSchemeEffective")}
      </MotifModalHeader>
      <MotifModalBody>
        {isLoading ? (
          <MotifProgressLoader show variant="default" />
        ) : (
          <div className="scheme-effective-status">
            {uploadStatus === "Success" ? (
              <div className="success-message">
                <div className="icon-container">
                  <MotifIcon
                    iconFunction={IconoirCheckCircle}
                    stroke="#4CAF50"
                    strokeWidth="1"
                    size="28"
                  />
                </div>
                <div className="message-title">
                  {t("pages.configuration.scheme.schemeEffectiveSuccess", {
                    schemeName: schemeName,
                  })}
                </div>
              </div>
            ) : uploadStatus === "error" ? (
              <div className="error-message">
                <div className="icon-container">
                  <MotifIcon
                    iconFunction={MotifAlertIcErrorOutline24px}
                    stroke="#FF3D00"
                    strokeWidth="1"
                    size="28"
                  />
                </div>
                <div className="message-title">
                  {t(
                    "pages.configuration.scheme.makeSchemeEffectiveErrorMessage"
                  )}
                </div>
                <div className="message-description">{errorMessage}</div>
              </div>
            ) : (
              <div className="loader-container">
                <span className="loader"></span>
                <div className="import-status">
                  {t("pages.configuration.scheme.effectiveLoaderTitle")}
                </div>
                {data?.processingStatusMessage &&
                  [1, 2].includes(data?.processingStatus) && (
                    <div className="import-status">
                      {data.processingStatusMessage}
                    </div>
                  )}
              </div>
            )}
          </div>
        )}
      </MotifModalBody>
    </MotifModal>
  );
};
export default SchemeEffectivePopup;
